// src/shell/modules/ModuleLoader.tsx
import React, { useEffect, useState, useRef } from 'react';
import { getModuleRecord, isModuleEnabled, toggleModule } from './ModuleRegistry.js';
import { computeSha256, verifyEd25519Signature, TRUSTED_DEV_PUBLIC_KEY_B64 } from '../crypto/verify-package.js';
import { ScopedHostProvider } from './ScopedHostProvider';
import { AlertOctagon, ShieldAlert } from 'lucide-react';

export interface RemoteModuleProps {
  moduleId: string;
  fallback?: React.ReactNode;
  [key: string]: unknown;
}

export const RemoteModule: React.FC<RemoteModuleProps> = ({
  moduleId,
  fallback,
  ...restProps
}) => {
  const [LoadedComponent, setLoadedComponent] = useState<React.ComponentType<any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manifest, setManifest] = useState<any | null>(null);
  const [enabled, setEnabled] = useState(() => isModuleEnabled(moduleId));

  useEffect(() => {
    const onToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ moduleId: string; enabled: boolean }>;
      if (customEvent.detail.moduleId === moduleId) {
        setEnabled(customEvent.detail.enabled);
      }
    };
    window.addEventListener('module-toggled', onToggle);
    return () => window.removeEventListener('module-toggled', onToggle);
  }, [moduleId]);

  useEffect(() => {
    let unmounted = false;

    async function loadSecureModule() {
      try {
        setError(null);
        const record = getModuleRecord(moduleId);
        if (!record) {
          throw new Error(`Module ${moduleId} is not registered in catalog.`);
        }

        // 1. Fetch Manifest
        const manifestUrl = record.bundleUrl.replace(/dist\/bundle\.js$/, 'manifest.signed.json');
        const mRes = await fetch(manifestUrl);
        if (!mRes.ok) throw new Error(`Failed to load manifest for ${moduleId}`);
        const manifestJson = await mRes.json();

        // 2. Fetch Bundle Buffer
        const bRes = await fetch(record.bundleUrl);
        if (!bRes.ok) throw new Error(`Failed to fetch bundle binary for ${moduleId}`);
        const bundleBytes = await bRes.arrayBuffer();

        // 3. Digest Validation
        const calculatedDigest = await computeSha256(bundleBytes);
        const expectedDigest = manifestJson.bundleDigest ? `sha256:${manifestJson.bundleDigest}` : undefined;
        if (expectedDigest && expectedDigest !== calculatedDigest) {
          throw new Error(
            `INTEGRITY ERROR: Digest mismatch!\nExpected: ${expectedDigest}\nActual: ${calculatedDigest}`
          );
        }

        // 4. Create Blob & Import Component
        const blob = new Blob([bundleBytes], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        const moduleExports = await import(/* @vite-ignore */ blobUrl);
        URL.revokeObjectURL(blobUrl);

        if (!unmounted) {
          setManifest(manifestJson);
          setLoadedComponent(() => moduleExports.default || moduleExports.WallModule || moduleExports[Object.keys(moduleExports)[0]]);
        }
      } catch (err: any) {
        if (!unmounted) setError(err.message || 'Verification failure');
      }
    }

    loadSecureModule();
    return () => { unmounted = true; };
  }, [moduleId]);

  if (error) {
    return (
      <div data-testid="module-integrity-error-banner" className="p-6 border border-rose-800/60 bg-rose-950/30 rounded-xl text-rose-300">
        <div className="flex items-center gap-2 font-bold mb-2 text-rose-200">
          <ShieldAlert className="w-5 h-5 text-rose-400" />
          Module Security Gate Exception
        </div>
        <pre className="text-xs font-mono bg-black/40 p-3 rounded border border-rose-900/50 whitespace-pre-wrap">
          {error}
        </pre>
      </div>
    );
  }

  if (!LoadedComponent) {
    return <>{fallback || <div>Verifying & loading module...</div>}</>;
  }

  if (!enabled) {
    return (
      <div data-testid="module-disabled-banner" style={{ padding: '24px', border: '1px solid rgba(245, 158, 11, 0.4)', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '12px', color: '#fef3c7', margin: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, marginBottom: '8px' }}>
          <AlertOctagon size={20} color="#fbbf24" />
          Operator Disabled
        </div>
        <div style={{ fontSize: '13px', color: '#fde68a', marginBottom: '16px' }}>
          Module <strong>{moduleId}</strong> has been administratively disabled. Its capabilities are currently suspended.
        </div>
        <button
          data-testid="module-enable-btn"
          onClick={() => toggleModule(moduleId, true)}
          style={{
            padding: '8px 16px',
            background: '#fbbf24',
            color: '#78350f',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          Toggle On
        </button>
      </div>
    );
  }

    return (
      <ScopedHostProvider moduleId={moduleId} permissions={manifest?.manifest?.permissions || manifest?.permissions || []}>
        <LoadedComponent {...restProps} />
      </ScopedHostProvider>
    );
  };
  
  export const ModuleLoader = RemoteModule;
  export default RemoteModule;
